/**
 * The audio store in the console (ADR-020 in platform): what an upload must be before it is sent,
 * how a setting's value names a file, and how a file and its uses are described. Pure, so
 * `test/audio.test.ts` pins it.
 *
 * A setting that plays a sound — a line's entry announcement and hold music, a campaign's
 * safe-harbor message, a voicemail message — holds the file's URL, `<base>/audio/<uuid>.wav`,
 * because that is what the switch fetches. The console never builds one: it copies `url` off a file
 * the server listed. It only has to recognise one, to show which file a setting names.
 */
import type { AudioFile, AudioUse } from "./types.js";

/** `STORE_URL_RE` in platform's `services/shared/telephony/media.py`. */
const STORE_URL =
  /^https?:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:\/[A-Za-z0-9._~-]+)*\/audio\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.wav$/;

/** The store file a setting's value names, by its uuid, or null for any other value. */
export function storeAudioUuid(value: string | null | undefined): string | null {
  return STORE_URL.exec((value ?? "").trim())?.[1] ?? null;
}

/** A tone the switch generates instead of playing a file — `TONES` in the platform's
 *  `services/shared/telephony/media.py`, which is the only place the value may come from: a
 *  teletone script carries a comma and a semicolon, and the platform allows these by value, not by
 *  shape. `;loops=-1` is what makes it last the whole wait instead of ringing once. */
export const RINGBACK_US = "tone_stream://%(2000,4000,440,480);loops=-1";

/** The tones a setting may be set to, by the label the console shows for each. */
export const TONES: readonly { value: string; label: string }[] = [
  { value: RINGBACK_US, label: "Ringing tone" },
];

export function isTone(value: string | null | undefined): boolean {
  return TONES.some((t) => t.value === value);
}

/** What a setting's value is, for the picker to show. */
export type PickedAudio =
  | { kind: "none" }
  /** A file in the store. Matched on the uuid, so a file still shows by name if the address the
   *  platform serves the store from has changed since the setting was saved. */
  | { kind: "file"; file: AudioFile }
  /** A store URL the list has no file for: deleted from under it, or another tenant's. */
  | { kind: "missing"; uuid: string }
  /** A value from before the store: a path on the switch, or a `local_stream://`. */
  | { kind: "other"; value: string };

/** `defaultValue` is what the server puts back when the setting is cleared — a line's hold music
 *  comes back as `local_stream://moh` — and it reads as no choice, like an empty value. */
export function pickedAudio(
  value: string | null | undefined,
  files: readonly AudioFile[],
  defaultValue?: string,
): PickedAudio {
  const v = (value ?? "").trim();
  if (v === "" || v === defaultValue) return { kind: "none" };
  const uuid = storeAudioUuid(v);
  if (uuid === null) return { kind: "other", value: v };
  const file = files.find((f) => f.uuid === uuid);
  return file ? { kind: "file", file } : { kind: "missing", uuid };
}

/** core-api's default ceiling on one upload (`DEFAULT_MAX_UPLOAD_BYTES`). */
export const MAX_AUDIO_BYTES = 50 * 1024 * 1024;

const WAV_TYPES = new Set(["audio/wav", "audio/x-wav", "audio/wave", "audio/vnd.wave"]);

/**
 * Why a chosen file would be refused, said before it is sent — or null. Only WAV is offered because
 * only WAV plays on the switch. The server reads the file's header and has the last word: an MP3
 * renamed `.wav` passes here and is refused there, with a reason.
 */
export function audioFileProblem(file: { name: string; size: number; type: string }): string | null {
  if (file.size === 0) return "The file is empty.";
  if (file.size > MAX_AUDIO_BYTES) {
    return `The file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_AUDIO_BYTES)}.`;
  }
  const wav = /\.wave?$/i.test(file.name) || WAV_TYPES.has(file.type);
  return wav ? null : "Only WAV files play on the phone system. Export the audio as WAV first.";
}

/** A name to start from: the file's own, without its extension. */
export function audioNameFromFile(fileName: string): string {
  return fileName.replace(/\.[^./\\]+$/, "").trim();
}

/** Tags as typed, comma-separated: trimmed, blanks dropped, repeats (ignoring case) dropped. */
export function parseTags(text: string): string[] {
  const tags: string[] = [];
  for (const part of text.split(",")) {
    const tag = part.trim();
    if (tag && !tags.some((t) => t.toLowerCase() === tag.toLowerCase())) tags.push(tag);
  }
  return tags;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A clip's length as a player shows it: `0:07`, `12:03`. A dash when the header did not say. */
export function formatClipLength(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "—";
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

const SETTINGS: Record<string, string> = {
  moh: "Hold music",
  entry_announcement: "Entry announcement",
  outbound_hold_media: "Hold for an answered lead",
  safe_harbor_media: "Safe-harbor message",
  media: "Voicemail message",
};

/** Where a file plays, in a person's words: "Hold music on line intake". */
export function audioUseText(use: AudioUse): string {
  const setting = SETTINGS[use.setting] ?? use.setting;
  if (use.kind === "voicemail_message") return `Voicemail message ${use.name}`;
  if (use.kind === "campaign") return `${setting} of ${use.name}`;
  return `${setting} on line ${use.name}`;
}

/** Where the setting a file plays in is changed: a line on its campaign's page, a campaign's
 *  safe-harbor message on its Dialing tab, a voicemail message in the library. Null for a line that
 *  belongs to no campaign, which has no page in the console. */
export function audioUsePath(use: AudioUse): string | null {
  if (use.kind === "voicemail_message") return "/voicemail-messages";
  if (!use.campaign_id) return null;
  return `/campaigns/${use.campaign_id}${use.kind === "campaign" ? "?tab=dialing" : ""}`;
}
